<script lang="ts">
  import * as THREE from "three";
  import { CSS3DObject, CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";
  import { onMount } from "svelte";
  import { ship } from "../net/ship.svelte.js";
  import { DECK, type NodeId } from "@spacecrew/shared";
  import CrtScreen from "../components/CrtScreen.svelte";
  import OverlayPlate from "../components/OverlayPlate.svelte";
  import { screenSignal } from "../lib/signal.js";
  import { session } from "../net/session.svelte.js";
  import { sharedUniforms } from "./psx/PsxMaterial.js";
  import { PsxComposer } from "./psx/PsxComposer.js";
  import { buildReactorRoom, type StationKind } from "./ReactorRoom.js";
  import { buildGangway } from "./GangwayRoom.js";
  import { audio } from "../audio/bus.js";
  import type { FixtureSpec } from "./fixtures.js";

  /** The DOM panels to mount into the scene (rendered by the parent as snippets). */
  let { screen, monitors, look = true, room: roomKind = "reactor", caption = "" }: { screen: import("svelte").Snippet; monitors?: import("svelte").Snippet; look?: boolean; room?: StationKind | "gangway"; caption?: string } = $props();

  // PSX resolution. 320×240 is canon; we keep the 4:3 target and letterbox the viewport.
  // PSX kept a 320×240 framebuffer; we render a little finer so the room reads as a room, and soften
  // the vertex snapping separately (see sharedUniforms) — the crawl is what made motion feel wrong.
  const W = 480, H = 360;
  let host: HTMLDivElement;
  let glCanvas: HTMLCanvasElement;
  let cssLayer: HTMLDivElement;
  let cssTopLayer: HTMLDivElement;
  // The fixture under the cursor: its caption, and where to put it on screen.
  let touch = $state<{ label: string; x: number; y: number } | null>(null);
  let screenEl: HTMLDivElement;
  let monitorsEl: HTMLDivElement;
  let plateEl: HTMLDivElement;

  // Scale of the DOM panels: CSS px per metre. The CRT face is 0.72 × 0.54 m → 640 × 480 px.
  const PX_PER_M = 640 / 0.72;

  const snap = $derived(ship.snap);
  const me = $derived(snap?.crew[session.sessionId]);
  const leverDown = $derived(!!snap && Object.values(snap.surge.handles).some((h) => h.heldBy === session.sessionId));
  /** How well the ship's own screens are holding up (1 = pristine). Drives the glass and the room's lamp. */
  const signal = $derived(screenSignal(snap));
  const redAlert = $derived(!!snap && (snap.reserve <= 10 || snap.breakers.status !== "idle"));

  onMount(() => {
    const renderer = new THREE.WebGLRenderer({ canvas: glCanvas, antialias: false, powerPreference: "low-power" });
    renderer.setPixelRatio(1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const shared = sharedUniforms(W, H, roomKind === "gangway" ? 0.55 : roomKind === "bridge" ? 0.52 : 0.46);
    if (roomKind === "bridge") { shared.uLightColor.value.set(0.75, 0.85, 1.1); shared.uAmbient.value.set(0.36, 0.42, 0.55); }
    if (roomKind === "breaker_bay") { shared.uLightColor.value.set(1.2, 0.55, 0.45); }
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(roomKind === "gangway" ? 60 : 48, W / H, 0.05, 20);
    /**
     * Wheel zoom is a camera dolly between two hand-authored framings — leaning in to read the
     * glass, or sitting back to see the room — never a browser zoom. FOV narrows as you lean in so
     * it feels like moving, not like a lens.
     */
    const FAR = roomKind === "gangway"
      ? { pos: new THREE.Vector3(-0.2, 0.16, 0.8), look: new THREE.Vector3(0.1, 0.0, -2.4), fov: 64 }
      : { pos: new THREE.Vector3(0, 0.58, 0.95), look: new THREE.Vector3(0, 0.05, -1.6), fov: 56 };
    const NEAR_FOV = roomKind === "gangway" ? 46 : 42;
    // Leaning in aims at the glass, wherever it is — so an off-centre kiosk stays framed as you approach.
    const nearPos = new THREE.Vector3(), nearLook = new THREE.Vector3();
    const camPos = new THREE.Vector3(), camLook = new THREE.Vector3();
    let zoom = 0.35, zoomTarget = 0.35;
    camera.position.set(0, 0.5, 0.55); // seated eye height, a forearm back from the desk edge
    const room = roomKind === "gangway" ? buildGangway(shared) : buildReactorRoom(shared, roomKind);
    scene.add(room.group);
    const composer = new PsxComposer(renderer, W, H);

    // DOM layer: same camera, panels placed at the anchors.
    const css = new CSS3DRenderer({ element: cssLayer });
    const cssScene = new THREE.Scene();
    // The hung pane gets its own CSS3D layer, composited above the room's. Inside one layer the
    // browser depth-sorts whole elements by their centre, so the tube panel — a 640×480 plane whose
    // centre sits nearer than the pane's — paints over the pane's right half and slices it. The pane
    // is an arm's length from the face: it is always in front, and a second layer says exactly that.
    const cssTop = new CSS3DRenderer({ element: cssTopLayer });
    const cssTopScene = new THREE.Scene();
    room.group.updateMatrixWorld(true);
    const place = (obj: CSS3DObject, anchor: THREE.Object3D, zOffset: number, scale = 1, top = false) => {
      anchor.updateMatrixWorld(true);
      obj.position.setFromMatrixPosition(anchor.matrixWorld);
      obj.quaternion.setFromRotationMatrix(anchor.matrixWorld);
      obj.translateZ(zOffset);
      obj.scale.setScalar(scale / PX_PER_M);
      (top ? cssTopScene : cssScene).add(obj);
    };
    // The gangway kiosk's glass is smaller than the reactor tube's: fit the panel to it.
    const screenObj = new CSS3DObject(screenEl); place(screenObj, room.anchors.crt, 0.002, roomKind === "gangway" ? 0.88 : 1);
    // The near framing is derived from the glass: a fixed distance along the direction it faces.
    nearLook.setFromMatrixPosition(room.anchors.crt.matrixWorld);
    nearPos.copy(nearLook).addScaledVector(
      new THREE.Vector3(0, 0, 1).applyQuaternion(room.anchors.crt.getWorldQuaternion(new THREE.Quaternion())),
      roomKind === "gangway" ? 0.78 : 0.95,
    );
    if (monitors) { const monObj = new CSS3DObject(monitorsEl); place(monObj, room.anchors.intercom, 0.14, 0.7); }
    // The overlay plate is clipped 10 cm in front of the glass — the depth gap is what parallaxes.
    // The pane is its own fixture on a ceiling arm — mounted to its own anchor, not to the tube.
    const plateObj = new CSS3DObject(plateEl);
    place(plateObj, room.anchors.plate, 0.012, roomKind === "gangway" ? 0.41 : 0.44, true);
    // CSS3DObject forces `pointer-events: auto` inline, which beats any class rule — the plate sits
    // in front of the terminal, so without this it swallows every click meant for the screen.
    plateEl.style.pointerEvents = "none";
    const paneGroup = room.anchors.plate.parent!; // hangPlate mounts the DOM anchor inside the fixture

    /**
     * Look-around. Three things make this feel like a head rather than a cursor leash:
     * a dead zone plus an eased curve (small movements near centre barely register, the extremes
     * still reach), critically-damped smoothing that is frame-rate independent, and an idle drift so
     * the frame is never perfectly still.
     */
    let yaw = 0, pitch = 0, tYaw = 0, tPitch = 0, vYaw = 0, vPitch = 0;
    let tDrift = 0; // the idle-drift clock, paused while a field is focused
    const DEAD = 0.08;
    const ease = (v: number) => {
      const a = Math.abs(v);
      if (a < DEAD) return 0;
      const t = (a - DEAD) / (1 - DEAD);
      return Math.sign(v) * t * t * (3 - 2 * t); // smoothstep: gentle near centre, full at the edges
    };
    const onMove = (e: PointerEvent) => {
      if (!look) return;
      const r = host.getBoundingClientRect();
      const damp = 1 - zoom * 0.65; // leaned in, the same travel would swing the glass off frame
      tYaw = -ease(((e.clientX - r.left) / r.width - 0.5) * 2) * 0.26 * damp;
      tPitch = -ease(((e.clientY - r.top) / r.height - 0.5) * 2) * 0.15 * damp;
    };
    const onLeave = () => { tYaw = 0; tPitch = 0; };
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);

    /**
     * Reaching for things. The pick runs off the last pointer position once per frame rather than
     * per pointermove — the camera is always drifting, so what is under the cursor changes even
     * when the mouse is still.
     */
    const ndc = new THREE.Vector2(2, 2); // off-screen until the pointer has been somewhere
    const trackPointer = (e: PointerEvent) => {
      const r = glCanvas.getBoundingClientRect();
      ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    };
    host.addEventListener("pointermove", trackPointer);
    host.addEventListener("pointerleave", () => { ndc.set(2, 2); });
    const onDown = (e: PointerEvent) => {
      // Only bare room clicks: the DOM panels sit above the canvas and handle their own.
      if (e.button !== 0 || (e.target as HTMLElement).closest(".panel")) return;
      trackPointer(e);
      const spec = room.fixtures.pick(ndc, camera) ?? held;
      if (!spec) return;
      const { hook } = room.fixtures.activate(spec, performance.now() / 1000);
      audio.play(hook);
      if (spec.id === "reactor.lamptest") lampTestUntil = performance.now() / 1000 + 1.1;
    };
    host.addEventListener("pointerdown", onDown);
    let lampTestUntil = 0;
    let held: FixtureSpec | null = null, heldUntil = 0;

    // The terminal is scrollable: over its text the wheel scrolls, over the room it zooms. Only
    // hand the wheel to the camera once the panel has nothing left to scroll in that direction.
    const onWheel = (e: WheelEvent) => {
      const scroller = (e.target as HTMLElement | null)?.closest?.(".crt-content") as HTMLElement | null;
      if (scroller && scroller.scrollHeight > scroller.clientHeight + 1) {
        const atTop = scroller.scrollTop <= 0;
        const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
        if (!((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom))) {
          // Chrome will not reliably scroll a box inside a matrix3d/preserve-3d subtree, so drive it.
          e.preventDefault();
          scroller.scrollTop = Math.max(0, Math.min(scroller.scrollHeight - scroller.clientHeight, scroller.scrollTop + e.deltaY));
          return;
        }
      }
      e.preventDefault();
      const step = e.deltaMode === 1 ? 0.12 : Math.min(0.18, Math.abs(e.deltaY) / 500 + 0.05);
      zoomTarget = Math.max(0, Math.min(1, zoomTarget - Math.sign(e.deltaY) * step));
    };
    host.addEventListener("wheel", onWheel, { passive: false });

    /**
     * Focus scrolling, driven by us.
     *
     * The terminal is a scrolling box inside a `matrix3d` subtree, and Chrome's scroll-into-view
     * math does not survive that: focusing a field (or typing into one) can scroll the panel to an
     * offset with no content in it — the tube reads as black and stays black, because nothing
     * scrolls it back. Clamp anything the browser does to the panel, and put the focused field in
     * view ourselves, working in content pixels (rect deltas divided by the panel's own scale).
     */
    const scroller = (): HTMLElement | null => screenEl.querySelector(".crt-content");
    const clampScroll = (el: HTMLElement) => {
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      const top = Math.max(0, Math.min(max, el.scrollTop));
      if (top !== el.scrollTop) el.scrollTop = top;
      if (el.scrollLeft !== 0) el.scrollLeft = 0;      // the terminal never scrolls sideways
    };
    const onScrollCapture = (e: Event) => {
      const el = e.target as HTMLElement | null;
      if (el && el.classList?.contains("crt-content")) clampScroll(el);
    };
    host.addEventListener("scroll", onScrollCapture, true);
    const onFocusIn = (e: FocusEvent) => {
      const el = scroller();
      const target = e.target as HTMLElement | null;
      if (!el || !target || !el.contains(target)) return;
      requestAnimationFrame(() => {
        clampScroll(el);
        const box = el.getBoundingClientRect(), field = target.getBoundingClientRect();
        const scale = box.height / Math.max(1, el.clientHeight) || 1;   // CSS3D scales the whole panel
        const top = (field.top - box.top) / scale;                      // content px from the visible top
        const h = field.height / scale;
        const margin = 24;
        if (top < margin) el.scrollTop += top - margin;
        else if (top + h > el.clientHeight - margin) el.scrollTop += top + h - el.clientHeight + margin;
        clampScroll(el);
      });
    };
    host.addEventListener("focusin", onFocusIn);

    const resize = () => {
      const w = host.clientWidth, h = host.clientHeight;
      // Letterbox to 4:3 so the PSX pixels stay square.
      let vw = w, vh = Math.round(w * 3 / 4); if (vh > h) { vh = h; vw = Math.round(h * 4 / 3); }
      renderer.setSize(vw, vh, false);
      glCanvas.style.width = `${vw}px`; glCanvas.style.height = `${vh}px`;
      css.setSize(vw, vh);
      cssTop.setSize(vw, vh);
      cssLayer.style.width = `${vw}px`; cssLayer.style.height = `${vh}px`;
      cssTopLayer.style.width = `${vw}px`; cssTopLayer.style.height = `${vh}px`;
      camera.aspect = 4 / 3; camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(host);

    let raf = 0, t0 = performance.now(), tPrev = t0, lastBlur = "";
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const now = performance.now();
      const t = (now - t0) / 1000;
      const dt = Math.min(0.05, (now - tPrev) / 1000); tPrev = now; // clamp so a stall cannot lurch
      // Critically damped spring — the same feel at 60 Hz and 144 Hz, and it settles without ringing.
      const stiff = 42, damp2 = 2 * Math.sqrt(stiff);
      vYaw += ((tYaw - yaw) * stiff - vYaw * damp2) * dt; yaw += vYaw * dt;
      vPitch += ((tPitch - pitch) * stiff - vPitch * damp2) * dt; pitch += vPitch * dt;
      zoom += (zoomTarget - zoom) * (1 - Math.exp(-dt / 0.10));
      camera.position.copy(camPos.lerpVectors(FAR.pos, nearPos, zoom));
      // Head-sway: a few centimetres of real translation, so depths separate. Rotation alone cannot
      // parallax — everything would swing across the frame together.
      camera.position.x += -yaw * 0.5;
      camera.position.y += pitch * 0.3;
      camera.lookAt(camLook.lerpVectors(FAR.look, nearLook, zoom));
      const fov = FAR.fov + (NEAR_FOV - FAR.fov) * zoom;
      if (Math.abs(camera.fov - fov) > 0.02) { camera.fov = fov; camera.updateProjectionMatrix(); }
      /*
       * Idle drift: two slow, unrelated frequencies per axis plus a whisper of roll, so the frame is
       * never mechanically still — a seated human, not a tripod.
       *
       * It stops dead while a field on the terminal has focus. Filling in a code or a callsign is
       * the one time the player is holding still and reading a fixed point, and involuntary motion
       * then reads as the monitor sliding away rather than as a body breathing. Pointer look-around
       * is untouched: what you ask for still happens, what you did not ask for does not. The drift
       * runs on its own clock so freezing it holds the pose instead of snapping the phase.
       */
      const el = document.activeElement;
      const readingAField = !!el && host.contains(el) && /INPUT|TEXTAREA|SELECT/.test(el.tagName);
      if (!readingAField) tDrift += dt;
      const driftY = Math.sin(tDrift * 0.31) * 0.0055 + Math.sin(tDrift * 0.83) * 0.0022;
      const driftX = Math.sin(tDrift * 0.47 + 1.7) * 0.0042 + Math.sin(tDrift * 1.13) * 0.0016;
      camera.rotateY(yaw * 0.7 + driftY);
      camera.rotateX(pitch * 0.7 + driftX);
      camera.rotateZ(Math.sin(tDrift * 0.23) * 0.0022);
      // The plate is only legible from the back of the seat: it fades and blurs as you lean in.
      // Focus pull: sitting back, the eye is on the pane and the room goes soft; leaning in, the
      // room sharpens and the pane blurs out of focus and fades.
      // Gone well before the camera reaches it. The pane hangs between the seat and the console, so
      // leaning in dollies the camera through it: any later and you spend the default framing
      // staring at the back of a sheet of glass 13 cm from your face.
      const plateFocus = Math.max(0, Math.min(1, (0.20 - zoom) / 0.16));
      // The fixture goes with the print — frame, drop arm and backlight included.
      paneGroup.visible = plateFocus > 0.001;
      plateEl.style.setProperty("--plate", plateFocus.toFixed(3));
      // `blur(0px)` is not free: a filter — even a zero-radius one — forces the browser to flatten
      // and separately rasterise the layer it is on, and these layers are DOM panels inside a
      // preserve-3d subtree. Hand it `none` at rest instead of a no-op blur.
      const farBlur = (plateFocus * 2.4).toFixed(2);
      if (farBlur !== lastBlur) {
        host.style.setProperty("--far-blur", farBlur === "0.00" ? "none" : `blur(${farBlur}px)`);
        lastBlur = farBlur;
      }
      // Fixtures: what is under the cursor, and everything that is still travelling. The camera is
      // never quite still, so a switch slides out from under a stationary cursor — hold the last
      // hover for a moment rather than letting the label strobe.
      const picked = ndc.x <= 1 ? room.fixtures.pick(ndc, camera) : null;
      if (picked) { held = picked; heldUntil = t + 0.25; }
      else if (t > heldUntil) held = null;
      const hovered = held;
      room.fixtures.setHover(hovered);
      host.style.cursor = hovered ? "pointer" : "";
      if (hovered) {
        const p = new THREE.Vector3().setFromMatrixPosition(hovered.hit.matrixWorld).project(camera);
        const r = glCanvas.getBoundingClientRect(), hr = host.getBoundingClientRect();
        touch = { label: hovered.label, x: r.left - hr.left + ((p.x + 1) / 2) * r.width, y: r.top - hr.top + ((1 - p.y) / 2) * r.height };
      } else if (touch) touch = null;
      room.fixtures.update(dt, t);
      for (const fan of room.motion.fan) fan.rotation.x += dt * 5.2;
      for (const n of room.motion.needles) n.mesh.rotation.z = n.home + Math.sin(t * n.rate) * n.span + Math.sin(t * n.rate * 7.3) * 0.012;
      for (const f of room.motion.flicker) {
        const lit = lampTestUntil > t || Math.random() > 0.11;
        (f.material as THREE.ShaderMaterial).uniforms.uTint!.value.set(lit ? 0xffe6b8 : 0x2a2418);
      }
      if (roomKind === "gangway") { // the caged lamp flickers; the kiosk lamp blinks slowly
        (room.anchors.beacon.material as THREE.ShaderMaterial).uniforms.uTint!.value.set(Math.random() > 0.06 ? 0xffc070 : 0x402810);
        (room.anchors.lamp.material as THREE.ShaderMaterial).uniforms.uTint!.value.set(Math.sin(t * 2) > 0.6 ? 0xff3322 : 0x331111);
      } else {
      room.anchors.lever.rotation.x = THREE.MathUtils.lerp(room.anchors.lever.rotation.x, leverDown ? 0.55 : -0.35, 0.2);
      // The room dims with the picture: a browning-out terminal takes the console with it.
      shared.uScreenPower.value += ((0.45 + signal * 0.95) - shared.uScreenPower.value) * 0.1;
      (room.anchors.beacon.material as THREE.ShaderMaterial).uniforms.uTint!.value.set(redAlert && Math.sin(t * 6) > 0 ? 0xff2211 : 0x330000);
      (room.anchors.lamp.material as THREE.ShaderMaterial).uniforms.uTint!.value.set(
        lampTestUntil > t ? 0xff6655 : snap?.breakers.status !== "idle" ? 0xff3322 : 0x331111);
      }
      composer.render(scene, camera);
      css.render(cssScene, camera);
      cssTop.render(cssTopScene, camera);
    };
    frame();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); host.removeEventListener("scroll", onScrollCapture, true); host.removeEventListener("focusin", onFocusIn); host.removeEventListener("pointermove", onMove); host.removeEventListener("pointermove", trackPointer); host.removeEventListener("pointerdown", onDown); host.removeEventListener("pointerleave", onLeave); host.removeEventListener("wheel", onWheel); composer.dispose(); room.dispose(); renderer.dispose(); };
  });
</script>

<div class="station" bind:this={host}>
  <canvas bind:this={glCanvas} class="gl"></canvas>
  <div bind:this={cssLayer} class="css"></div>
  <div bind:this={cssTopLayer} class="css top"></div>
  <!-- Panels live in the DOM flow only until mounted into the CSS3D scene. -->
  <div bind:this={screenEl} class="panel tube" style="font-size: {roomKind === 'gangway' ? 11 : 13}px">
    <CrtScreen {signal} station={roomKind}>{@render screen()}</CrtScreen>
  </div>
  <div bind:this={monitorsEl} class="panel monitors">{#if monitors}{@render monitors()}{/if}</div>
  <div bind:this={plateEl} class="plate-panel"><OverlayPlate /></div>
  {#if touch}
    <!-- Label-maker tape stuck next to whatever your hand is on. -->
    <p class="touch" style="left: {touch.x}px; top: {touch.y}px">{touch.label}</p>
  {/if}
  <p class="hint dim">{caption || (me ? `${me.name} — ${DECK.nodes[(me.toNodeId || me.nodeId) as NodeId].label}` : "")} · move the mouse to look, scroll to lean in</p>
</div>

<style>
  .station { --far-blur: none; position: relative; width: 100%; height: 100dvh; background: #000; overflow: hidden; display: grid; place-items: center; }
  /* Everything at room depth defocuses together — the canvas and the DOM panels mounted in it. */
  .gl { image-rendering: pixelated; image-rendering: crisp-edges; grid-area: 1 / 1; filter: var(--far-blur); }
  .tube, .monitors { filter: var(--far-blur); }
  .css { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); pointer-events: none; }
  .css :global(> div) { pointer-events: none; }
  /* The pane's layer is composited over the room's, so it can never be sliced by the tube panel. */
  .css.top { z-index: 2; }
  .panel { pointer-events: auto; }
  .tube { width: 640px; height: 480px; }
  /* Decorative only: never intercepts a click meant for the terminal behind it. */
  .plate-panel { width: 640px; height: 480px; pointer-events: none; }
  .monitors { width: 620px; height: 185px; display: flex; justify-content: center; align-items: flex-start; background: transparent; font-size: 12px; }
  .hint { position: absolute; left: 1rem; bottom: 0.6rem; font-size: 0.8em; margin: 0; }
  /* The caption sits beside your hand, not in a HUD: worn tape, offset up and to the right. */
  .touch {
    position: absolute; z-index: 3; margin: 0; transform: translate(14px, -26px);
    pointer-events: none; white-space: nowrap;
    font-size: 0.62rem; letter-spacing: 0.18em; text-transform: uppercase;
    color: #1b1d19; background: #cfc7ae; padding: 0.16rem 0.42rem;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.5), 0 0 14px rgba(0, 0, 0, 0.6);
  }
  @media (prefers-reduced-motion: reduce) { .station { scroll-behavior: auto; } }
</style>
